import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { clientApi } from '../../services/client';
import type { SiteListItem } from '../../types';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

const statusVariant = (s: string) =>
  s === 'active' ? 'success' : s === 'pending' ? 'warning' : 'error';

const columns: Column<SiteListItem>[] = [
  { key: 'name', header: 'Site Name', sortable: true },
  { key: 'city', header: 'City', sortable: true },
  { key: 'state', header: 'State', sortable: true },
  { key: 'vendor_org_name', header: 'Vendor',
    render: (row) => <span>{row.vendor_org_name || '—'}</span>,
  },
  { key: 'guard_count', header: 'Guards', sortable: true },
  { key: 'score', header: 'Score', sortable: true,
    render: (row) => (
      <span className={`font-semibold ${getScoreColorClass(getScoreColor(row.score))}`}>
        {toGGScore(row.score)}
      </span>
    ),
  },
  { key: 'vendor_assignment_status', header: 'Status',
    render: (row) => <StatusBadge label={row.vendor_assignment_status} variant={statusVariant(row.vendor_assignment_status)} />,
  },
];

export default function ClientSites() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['client', 'sites', page],
    queryFn: () => clientApi.getSites({ page: String(page) }).then((r) => r.data),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader
        title="Sites"
        description="Manage your security sites"
        actions={
          <button
            onClick={() => navigate('/client/sites/new')}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700"
          >
            Add Site
          </button>
        }
      />
      <DataTable<SiteListItem>
        columns={columns}
        data={data?.results ?? []}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No sites yet"
        emptyDescription="Add your first site to get started."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/client/sites/${row.id}`)}
      />
    </>
  );
}
